import { Link, Outlet, useRouterState } from '@tanstack/react-router'
import { ConnectionStatus } from './ConnectionStatus'
import { Logo } from './Logo'
import { useCurrentUser, useIsAdmin, useSessionStore } from '@/lib/session'

interface NavItem {
  to: string
  label: string
  adminOnly?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard' },
  { to: '/inventory', label: 'Inventory' },
  { to: '/fixtures', label: 'Fixtures' },
  { to: '/locations', label: 'Locations' },
  { to: '/stocktake', label: 'Stocktake' },
  { to: '/users', label: 'Users', adminOnly: true },
]

/**
 * The persistent top nav (spec W-01): every screen is reachable from it, and
 * the active section is tinted in the plum accent. User management appears for
 * admins only (spec W-05).
 */
export function AppShell() {
  const isAdmin = useIsAdmin()
  const user = useCurrentUser()
  const signOut = useSessionStore((s) => s.signOut)
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  const isActive = (to: string) => (to === '/' ? pathname === '/' : pathname.startsWith(to))

  return (
    <div className="app-shell">
      <nav className="app-nav">
        <Link to="/" aria-label="MADS Theatre Tech Inventory — dashboard">
          <Logo size={30} />
        </Link>

        <div className="nav-links">
          {NAV_ITEMS.filter((item) => item.adminOnly !== true || isAdmin).map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`nav-link${isActive(item.to) ? ' is-active' : ''}`}
              aria-current={isActive(item.to) ? 'page' : undefined}
            >
              {item.label}
            </Link>
          ))}
        </div>

        {/*
          Before the actions rather than after them (issue #64): whether the app
          can reach the API is context for everything to its right, and reading
          it after pressing "Add equipment" is reading it too late.
        */}
        <ConnectionStatus />

        <Link to="/inventory/new" className="btn btn-primary">
          + Add equipment
        </Link>

        {/*
          Your own account (issue #14), kept out of NAV_ITEMS: it belongs beside
          signing out rather than among the sections of the inventory.
        */}
        {user !== null && (
          <Link
            to="/profile"
            className={`nav-link${isActive('/profile') ? ' is-active' : ''}`}
            aria-current={isActive('/profile') ? 'page' : undefined}
            title={`Signed in as ${user.email}`}
          >
            {user.name}
          </Link>
        )}

        {user !== null && (
          <button type="button" className="btn btn-ghost" onClick={signOut}>
            Sign out
          </button>
        )}
      </nav>

      <main className="app-main">
        <Outlet />
      </main>
    </div>
  )
}
